import get from 'lodash/get';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import { Mousewheel, Navigation } from 'swiper';
import {
  selectIsDesktop,
  selectIsMobile,
  selectIsSmallTablet,
  selectIsTablet,
} from 'redux/selectors/layout';
import { getDocumentFields, getImage } from 'utils/helper';
import { CASE_STUDIES, HEADER_HEIGHT, PAGES } from 'utils/constants';
import Card from 'UI/components/Cards/Card';
import Overlay from 'UI/containers/Overlay';
import { routes } from 'utils/routes';
import { LINK_TYPE } from 'utils/constants/linkType';
import styles from '../CardsSection.module.scss';

const SLIDES_PER_VIEW = {
  [routes.bookCall.slug]: {
    thirdSectionView: 'auto',
    fourthSectionView: 'auto',
  },
  [CASE_STUDIES.kitchenEquipment]: {
    firstSectionView: 'auto',
  },
  [CASE_STUDIES.aiPoweredComplianceTool]: {
    firstSectionView: 'auto',
  },
  [routes.aiAgentsServices.slug]: {
    secondSectionView: 2,
    thirdSectionView: 2,
  },
  [routes.llmDevelopment.slug]: {
    fourthSectionView: 1,
  },
  [routes.aiVoiceAssistantDevelopment.slug]: {
    fourthSectionView: 1,
    fifthSectionView: 1,
    thirdSectionView: 1,
  },
};

const SPECIFIC_SETTINGS = {
  [routes.aiCopilot.slug]: {
    firstSectionView: {
      breakpoints: {
        0: {
          slidesPerView: 1,
          slidesPerGroup: 1,
          centeredSlides: true,
        },
        768: {
          slidesPerView: 2,
          centeredSlides: false,
        },
        1024: {
          slidesPerView: 3,
          centeredSlides: false,
        },
      },
    },
    secondSectionView: {
      breakpoints: {
        0: {
          slidesPerView: 1,
          slidesPerGroup: 1,
          centeredSlides: true,
        },
      },
    },
    fifthSectionView: {
      breakpoints: {
        0: {
          slidesPerView: 1,
          slidesPerGroup: 1,
          centeredSlides: true,
        },
      },
    },
    seventhSectionView: {
      breakpoints: {
        0: {
          slidesPerView: 1,
          slidesPerGroup: 1,
          centeredSlides: true,
        },
      },
    },
  },
  [routes.ragDevelopment.slug]: {
    thirdSectionView: {
      breakpoints: {
        0: {
          slidesPerView: 1,
          slidesPerGroup: 1,
          centeredSlides: true,
        },
      },
    },
  },
};

const SPACE_BETWEEN = {
  [routes.bookCall.slug]: {
    thirdSectionView: 66,
    fourthSectionView: 43,
  },

  [CASE_STUDIES.digitalWallet]: {
    firstSectionView: 16,
  },

  [CASE_STUDIES.aiConcierge]: {
    firstSectionView: 16,
  },

  [CASE_STUDIES.aiVoiceAssistant]: {
    firstSectionView: 16,
  },
};

const REWIND = {
  [routes.bookCall.slug]: {
    firstSectionView: true,
    secondSectionView: true,
    thirdSectionView: true,
  },
};

const cardMapper = (withOverlay) => (card) => {
  if (card?.sys?.contentType?.sys?.id === 'article') {
    const {
      title,
      previewImageUrl,
      slug,
      svgWithWrapper,
    } = getDocumentFields(card, [
      'title',
      'previewImageUrl',
      'slug',
    ]);

    const image = getImage(previewImageUrl);

    return {
      url: `${PAGES.blog}/${slug}`,
      image,
      title,
      children: <span>Read more</span>,
    };
  }

  const {
    contentList,
    images,
    contentModules: cardContent,
    text,
    title,
    withOverlay: specificCardOverlay,
    ...rest
  } = getDocumentFields(
    card,
    [
      'title',
      'contentList',
      'text',
      'images',
      'imagesBundles',
      'contentModules',
      'withOverlay',
    ],
  );

  const image = getImage(get(images, '[0]'));
  const icon = get(contentList, '[0]');
  const url = get(cardContent, '[0].fields.url');

  const modules = (cardContent || []).filter(
    (item) => item?.sys?.contentType?.sys?.id !== 'link',
  );
  const contentModules = specificCardOverlay ? modules : undefined;

  const items = specificCardOverlay ? undefined : modules
    .map((item) => getDocumentFields(item, ['title', 'contentList']));

  const children = withOverlay ? (
    <Overlay className={styles.overlay}>
      <Card
        title={title}
        text={text}
      />
    </Overlay>
  ) : null;

  return {
    url,
    image,
    icon,
    text,
    title,
    children,
    items,
    contentModules,
    specificCardOverlay,
    ...rest,
  };
};

export default ({
  section,
  type,
  withSlider: sectionWithSlider,
  withOverlay,
  data,
  handleOnCTAClick: handleOnCTAClickCommon,
  ...rest
}) => {
  const {
    title,
    description,
    formattedDescription,
    subtitle,
    view,
    contentModules,
    images: rawImages,
  } = getDocumentFields(
    data || section,
    [
      'title',
      'description',
      'formattedDescription',
      'contentModules',
      'subtitle',
      'view',
      'images',
    ],
    {
      isNormilized: (data && !Object.keys(data).includes('fields'))
      || (section && !Object.keys(section).includes('fields')),
    },
  );

  const {
    contentModules: rawCardList,
    withoutBackground,
    disableSliderOnMobile,
    enableSliderOnDesktop,
    enableSliderOnMobile,
    svgWithWrapper,
  } = getDocumentFields(
    get(contentModules, '[0]', []),
    [
      'contentModules',
      'withoutBackground',
      'disableSliderOnMobile',
      'enableSliderOnDesktop',
      'enableSliderOnMobile',
      'svgWithWrapper',
    ],
  );

  const images = (rawImages || []).map((rawImage) => getImage(rawImage));

  const cardList = (rawCardList || []).map(cardMapper(withOverlay));

  const ctaLink = get(contentModules, '[1]');

  const isMobileResolution = useSelector(selectIsMobile);
  const isTabletResolution = useSelector(selectIsTablet);
  const IsFullResolution = useSelector(selectIsDesktop);
  const isSmallTabletResolution = useSelector(selectIsSmallTablet);

  const withSlider = sectionWithSlider
  || enableSliderOnDesktop
  || (!disableSliderOnMobile && (isTabletResolution || isSmallTabletResolution || isMobileResolution)) || (enableSliderOnMobile && isMobileResolution);

  const isShowNavigation = enableSliderOnDesktop || !(IsFullResolution && cardList?.length <= 3);

  const swiperProps = {
    modules: [Navigation, Mousewheel],
    slidesPerView: 1,
    spaceBetween: SPACE_BETWEEN[type]?.[view] || 32,
    centeredSlides: true,
    autoHeight: false,
    passiveListeners: true,
    rewind: REWIND[type]?.[view] || false,
    mousewheel: {
      forceToAxis: true,
    },
    breakpoints: {
      768: {
        slidesPerView: SLIDES_PER_VIEW[type]?.[view] || 2,
        centeredSlides: false,
      },
      1024: {
        slidesPerView: SLIDES_PER_VIEW[type]?.[view] || 3,
        centeredSlides: false,
      },
    },
    ...SPECIFIC_SETTINGS[type]?.[view],
  };

  const className = cn(
    styles.cardsSection,
    styles[type],
    styles[view],
    {
      [styles.withSlider]: withSlider,
    },
  );

  const handleOnCTAClick = (e) => {
    if (ctaLink.fields.type === LINK_TYPE.scrollToSection) {
      e.preventDefault();

      const element = document.getElementById(ctaLink.fields.url);

      window.scrollBy({
        top: element.getBoundingClientRect().top - HEADER_HEIGHT - 50,
        behavior: 'smooth',
      });
    } else {
      handleOnCTAClickCommon();
    }
  };

  const ctaLinkData = ctaLink?.fields?.type === LINK_TYPE.scrollToSection ? {
    ...ctaLink,
    fields: {
      ...ctaLink.fields,
      url: '',
    },
  } : ctaLink;

  return {
    type,
    view,
    withSlider,
    cardList,
    ctaLink: ctaLinkData,
    title,
    description,
    formattedDescription,
    subtitle,
    className,
    withoutBackground,
    swiperProps,
    withOverlay,
    isShowNavigation,
    images,
    handleOnCTAClick,
    svgWithWrapper,
    ...rest,
  };
};
